import { MessageTemplateType } from "@repo/app-types/entities/messageTemplate";
import { isValidEmail } from "@repo/app-types/helpers/utilities";
import { CreatableComboboxField } from "@repo/ui/combobox/creatableComboboxField";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import type { FC } from "react";
import { useMessageVariables } from "../../../settingsPage/messageTemplateSettings/useMessageTemplateVariables.js";

interface SchedulingConfigurationMeetingSettingsProps {
  meetingTitle: string;
  meetingDescription: string;
  meetingLocation: string;
  meetingRecipients: string[];
  onMeetingTitleChange: (value: string) => void;
  onMeetingDescriptionChange: (value: string) => void;
  onMeetingLocationChange: (value: string) => void;
  onMeetingRecipientsChange: (value: string[]) => void;
}

export const SchedulingConfigurationMeetingSettings: FC<
  SchedulingConfigurationMeetingSettingsProps
> = ({
  meetingTitle,
  meetingDescription,
  meetingLocation,
  meetingRecipients,
  onMeetingTitleChange,
  onMeetingDescriptionChange,
  onMeetingLocationChange,
  onMeetingRecipientsChange,
}) => {
  const { ref: titleRef, widget: titleVariablesWidget } =
    useMessageVariables<HTMLInputElement>({
      type: MessageTemplateType.NextMeetingInvite,
      value: meetingTitle,
      onChange: onMeetingTitleChange,
    });

  const { ref: descriptionRef, widget: descriptionVariablesWidget } =
    useMessageVariables<HTMLTextAreaElement>({
      type: MessageTemplateType.NextMeetingInvite,
      value: meetingDescription,
      onChange: onMeetingDescriptionChange,
    });

  const { ref: locationRef, widget: locationVariablesWidget } =
    useMessageVariables<HTMLInputElement>({
      type: MessageTemplateType.NextMeetingInvite,
      value: meetingLocation,
      onChange: onMeetingLocationChange,
    });

  return (
    <div className="space-y-4 pt-4">
      <Label size={Size.MEDIUM}>Meeting settings</Label>
      <div>
        <Input
          ref={titleRef}
          label="Meeting title"
          placeholder="Interview with [contact.first_name] [contact.last_name]"
          value={meetingTitle}
          onChange={(e) => onMeetingTitleChange(e.target.value)}
          maxLength={100}
        />
        <div className="mt-2">{titleVariablesWidget}</div>
      </div>
      <div>
        <Textarea
          ref={descriptionRef}
          label="Meeting description"
          placeholder="To prepare for your on-site interview, make sure to bring..."
          value={meetingDescription}
          onChange={onMeetingDescriptionChange}
          height="100px"
        />
        <div className="mt-2">{descriptionVariablesWidget}</div>
      </div>
      <div>
        <Input
          ref={locationRef}
          label="Meeting location"
          placeholder="123 Main St, Anytown, USA"
          value={meetingLocation}
          onChange={(e) => onMeetingLocationChange(e.target.value)}
          maxLength={100}
        />
        <div className="mt-2">{locationVariablesWidget}</div>
      </div>
      <div>
        <CreatableComboboxField
          label="Calendar invite recipients"
          placeholder="Add an internal email that should be notified of new interviews"
          value={meetingRecipients}
          onChange={(emails) => {
            if (emails.length > 5) {
              showToast({
                title:
                  "Limit of 5 recipients reached. Consider adding a distribution list email address instead.",
                variant: ToastVariant.Error,
              });
              return;
            }
            onMeetingRecipientsChange(
              emails.filter((email) => isValidEmail(email)),
            );
          }}
          description="After a meeting is scheduled, a calendar event with the meeting details and AI assessment results will be sent to these recipients. To notify the candidate, use advancement message templates."
        />
      </div>
    </div>
  );
};
